import { useState, useCallback } from 'react';

export interface UseKeyboardListNavigationOptions {
  /**
   * Whether to wrap around when reaching the end/beginning of the list
   * @default true
   */
  wrap?: boolean;
  /**
   * Initial selected index
   * @default 0
   */
  initialIndex?: number;
}

export interface UseKeyboardListNavigationResult {
  selectedIndex: number;
  setSelectedIndex: (index: number) => void;
  handleKeyDown: (event: React.KeyboardEvent) => void;
}

/**
 * Hook for managing keyboard navigation in lists
 * Handles arrow keys, enter, and escape key interactions
 */
export const useKeyboardListNavigation = <T>(
  items: T[],
  onSelect: (item: T) => void,
  onClose: () => void,
  options: UseKeyboardListNavigationOptions = {},
): UseKeyboardListNavigationResult => {
  const { wrap = true, initialIndex = 0 } = options;
  const [selectedIndex, setSelectedIndex] = useState(initialIndex);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setSelectedIndex((prev) => {
            if (wrap) {
              return (prev + 1) % items.length;
            }

            return Math.min(items.length - 1, prev + 1);
          });

          break;
        case 'ArrowUp':
          event.preventDefault();
          setSelectedIndex((prev) => {
            if (wrap) {
              return (prev - 1 + items.length) % items.length;
            }

            return Math.max(0, prev - 1);
          });

          break;
        case 'Enter':
          event.preventDefault();
          if (items[selectedIndex]) {
            onSelect(items[selectedIndex]);
          }

          break;
        case 'Escape':
          event.preventDefault();
          onClose();
          break;
      }
    },
    [items, selectedIndex, onSelect, onClose, wrap],
  );

  return { selectedIndex, setSelectedIndex, handleKeyDown };
};
